// Ported from packages/desk/src/mobile/QuickActions.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The small menu over a long-pressed icon: the app's name, then two to four actions, each a host callback (usually
 * "open this app with these launch params"). The rest of the screen dims behind it. The arrow keys move between the
 * actions, Escape, Back or a tap outside close it, and focus goes back to the icon.
 */
import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { placeMenu } from "./layout";
import { useBackToClose, useFocusTrap } from "./layers";
import type { QuickAction } from "./types";

export function QuickActions({
  name,
  icon,
  anchor,
  actions,
  onOpen,
  onClose,
}: {
  name: string;
  icon: ReactNode;
  /** The pressed icon. */
  anchor: HTMLElement;
  actions: QuickAction[];
  /** Always offered first: open the app as a tap would. */
  onOpen: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  useBackToClose(true, onClose);
  useFocusTrap(ref, true, onClose);

  useLayoutEffect(() => {
    const menu = ref.current;
    if (!menu) return;
    const r = anchor.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const p = placeMenu({ x: r.left, y: r.top, w: r.width, h: r.height }, { w: m.width || 240, h: m.height || 160 }, { w: window.innerWidth, h: window.innerHeight });
    setPos({ x: p.x, y: p.y });
  }, [anchor]);

  const run = (fn: () => void) => {
    onClose();
    fn();
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : e.key === "ArrowDown" ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next]!.focus();
  };

  return (
    <div className="rdr-mlayer rdr-mqa-layer" onClick={onClose}>
      <div
        ref={ref}
        className="rdr-mqa"
        role="menu"
        aria-labelledby={titleId}
        data-rdr-quick-actions=""
        // Unplaced for one frame: invisible but still focusable (a hidden element cannot take focus).
        style={pos ? { left: pos.x, top: pos.y } : { opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <div className="rdr-mqa-head">
          <span className="rdr-mqa-ic" aria-hidden="true">
            {icon}
          </span>
          <span id={titleId} className="rdr-mqa-name">
            {name}
          </span>
        </div>
        <button type="button" role="menuitem" className="rdr-mqa-item" data-autofocus="" onClick={() => run(onOpen)}>
          Open
        </button>
        {actions.map((a) => (
          <button key={a.id} type="button" role="menuitem" className="rdr-mqa-item" data-rdr-action={a.id} onClick={() => run(a.run)}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
